import { FC, PropsWithChildren, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddressAutofill } from "@mapbox/search-js-react";
import styled from "styled-components";
import { Button, Input, Spacer } from "@qivia/ui";
import { Page } from "../../components/Page";
import "../../../i18n/i18n";

interface AddressAutofillResult {
  features: { properties?: { full_address?: string } }[];
}

type AddressAutofillProps = PropsWithChildren<{
  accessToken: string;
  onRetrieve: (result: AddressAutofillResult) => void;
}>;

// @mapbox/search-js-react ships incompatible React types → cast to a valid JSX component
const AddressAutofillInput =
  AddressAutofill as unknown as FC<AddressAutofillProps>;

const handleAddressRetrieve =
  (setAddress: (value: string) => void) => (result: AddressAutofillResult) => {
    const fullAddress = result.features[0]?.properties?.full_address;
    if (fullAddress) {
      setAddress(fullAddress);
    }
  };

interface VehicleRestitutionReturnStepProps {
  departureLocation: string;
  setDepartureLocation: (value: string) => void;
  furthestDestination: string;
  setFurthestDestination: (value: string) => void;
  mileage: string;
  setMileage: (value: string) => void;
  onReturn: () => void;
  onSubmit: () => void;
}

export const VehicleRestitutionReturnStep = (
  props: VehicleRestitutionReturnStepProps,
) => {
  const { t } = useTranslation();
  const accessToken = import.meta.env["VITE_MAPBOX_TOKEN"] as string;
  const [displayError, setDisplayError] = useState(false);

  const isFormCompleted = useMemo(
    () =>
      props.departureLocation !== "" &&
      props.furthestDestination !== "" &&
      props.mileage !== "",
    [props.departureLocation, props.furthestDestination, props.mileage],
  );

  const onSubmit = () => {
    if (!isFormCompleted) {
      setDisplayError(true);
      return;
    }
    props.onSubmit();
  };

  return (
    <Page
      title={t("restitution.vehicleReturn.title")}
      returnButton
      returnButtonAction={props.onReturn}
      helpButton
      withoutHeaderSpacer
      bottomButton={
        <Button
          title={t("restitution.button.continue")}
          onClick={onSubmit}
          height={3.5}
          disabled={!isFormCompleted}
        />
      }
    >
      <StyledForm>
        <Spacer y={1} />
        <AddressAutofillInput
          accessToken={accessToken || ""}
          onRetrieve={handleAddressRetrieve(props.setDepartureLocation)}
        >
          <Input
            label={t("restitution.vehicleReturn.departureLocation.label")}
            placeholder={
              t("restitution.vehicleReturn.departureLocation.placeholder") || ""
            }
            value={props.departureLocation}
            onChange={(e) => props.setDepartureLocation(e.target.value)}
            error={
              displayError && props.departureLocation === ""
                ? t("address.error.input")
                : undefined
            }
            autoComplete="street-address"
          />
        </AddressAutofillInput>
        <AddressAutofillInput
          accessToken={accessToken || ""}
          onRetrieve={handleAddressRetrieve(props.setFurthestDestination)}
        >
          <Input
            label={t("restitution.vehicleReturn.furthestDestination.label")}
            placeholder={
              t("restitution.vehicleReturn.furthestDestination.placeholder") ||
              ""
            }
            value={props.furthestDestination}
            onChange={(e) => props.setFurthestDestination(e.target.value)}
            error={
              displayError && props.furthestDestination === ""
                ? t("address.error.input")
                : undefined
            }
            autoComplete="street-address"
          />
        </AddressAutofillInput>
        <Input
          label={t("restitution.vehicleReturn.mileage.label")}
          placeholder={t("restitution.vehicleReturn.mileage.placeholder") || ""}
          value={props.mileage}
          onChange={(e) => props.setMileage(e.target.value)}
          error={
            displayError && props.mileage === ""
              ? t("mileage.error.input")
              : undefined
          }
          type="number"
        />
      </StyledForm>
    </Page>
  );
};

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
